import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, Subscription as RxSubscription, of} from 'rxjs';
import {
  Ref,
  SearchResult,
  SimplePagination,
  SimplePaginationUtils,
  SortOrder,
  Subscription,
  SubscriptionFile,
  SubscriptionFileSearch,
  SubscriptionFileService,
  SubscriptionFileSortField,
} from '@lifeislife/lifeislife-domain';
import {debounceTime, map, publishReplay, refCount, switchMap, tap} from 'rxjs/operators';
import {CustomerContextService} from '../../service/customer-context-service';
import {LazyLoadEvent} from 'primeng/api';

@Component({
  selector: 'cf-subscription-document-list',
  templateUrl: './subscription-document-list.component.html',
  styleUrls: ['./subscription-document-list.component.scss'],
})
export class SubscriptionDocumentListComponent implements OnInit, OnDestroy {
  Array = Array;

  @Input()
  set subscriptionRef(ref: Ref<Subscription>) {
    this.subscriptionRefSource$.next(ref);
  }

  @Input()
  pageSize = 5;
  @Input()
  rowClickable = true;
  @Input()

  @Output()
  totalCountChange = new EventEmitter<number>();

  @Output()
  rowClick = new EventEmitter<SubscriptionFile>();

  subscriptionRefSource$ = new BehaviorSubject<Ref<Subscription>>(null);
  paginationSource$ = new BehaviorSubject<SimplePagination>(null);
  loading$ = new BehaviorSubject<boolean>(false);
  searchFilter$: Observable<SubscriptionFileSearch>;
  searchResults$: Observable<SearchResult<SubscriptionFile>>;

  private rxSubscription = new RxSubscription();

  constructor(
    private customerContextService: CustomerContextService,
    private subscriptionFileService: SubscriptionFileService,
  ) {
  }

  ngOnInit(): void {
    this.paginationSource$.next(SimplePaginationUtils.newPagination(this.pageSize, {
      field: SubscriptionFileSortField.FILE_CREATION_TIME,
      order: SortOrder.DESCENDING,
    }));

    this.searchFilter$ = this.subscriptionRefSource$.pipe(
      map(ref => this.createFilter(ref)),
      publishReplay(1), refCount(),
    );

    this.searchResults$ = combineLatest([this.paginationSource$, this.searchFilter$]).pipe(
      debounceTime(200),
      switchMap(r => this.searchFiles$(r[0], r[1])),
      publishReplay(1), refCount(),
    );

    const countSubscription = this.searchResults$.pipe(
      map(r => r == null ? null : r.count),
    ).subscribe(c => this.totalCountChange.next(c));
    this.rxSubscription = new RxSubscription();
    this.rxSubscription.add(countSubscription);
  }

  ngOnDestroy() {
    this.rxSubscription.unsubscribe();
  }

  onPaginationChange(event: LazyLoadEvent) {
    const curPagination = this.paginationSource$.getValue();
    const newPagiantion = SimplePaginationUtils.applyLazyLoadEvent(curPagination, event);
    this.paginationSource$.next(newPagiantion);
  }

  onRowClick(row: SubscriptionFile, event: MouseEvent) {
    this.rowClick.next(row);
  }

  private createFilter(ref: Ref<Subscription>) {
    const customerRef = this.customerContextService.getActiveCustomerRef();
    const searchFilter: SubscriptionFileSearch = {
      subscriptionSearch: {
        subscriptionRef: ref,
        customerSearch: {
          exactCustomerRef: customerRef,
        },
      },
      storedFileSearch: {
        deleted: false,
      },
      visibleToCustomer: true,
    };
    return searchFilter;
  }

  private searchFiles$(pagination: SimplePagination, searchFilter: SubscriptionFileSearch): Observable<SearchResult<SubscriptionFile>> {
    if (pagination == null || searchFilter == null) {
      return of(new SearchResult());
    }
    this.loading$.next(true);
    return this.subscriptionFileService.searchSubscriptionFiles$(searchFilter, pagination).pipe(
      tap(a => this.loading$.next(false)),
    );
  }

  //
  // private searchStoredFilesResults$(searchResults: SearchResult<SubscriptionFile>): Observable<SearchResult<StoredFile>> {
  //   const stoerFiles$List = searchResults.list.map(subFile => this.fileService.getFile$(subFile.storedFileRef));
  //   const storedFiles$ = stoerFiles$List.length === 0 ? of([]) : forkJoin(stoerFiles$List);
  //   return storedFiles$.pipe(
  //     map(filesList => Object.assign({}, searchResults, {
  //       list: filesList,
  //     } as Partial<SearchResult<StoredFile>>)),
  //   );
  // }


}
